import { useInstantSearch } from 'react-instantsearch';
import { type DateRange } from 'react-day-picker';
import { useEffect, useState } from 'react';
import { endOfDay, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { isCreatedAtClause, splitFilterClauses } from '@/utilities/search/filters';
import { DatePickerWithRange } from '@/components/date-picker-with-range';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useSearchClient } from '@/context/SearchClientContext';
import { useSearch } from '@/context/SearchContext';
import { EntityType } from '@/types/entity';

export const SearchWithDateFilter = () => {
  const [date, setDate] = useState<DateRange | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { searchIndexes } = useSearchClient();
  const { uiState } = useInstantSearch();
  const { navigateToResults } = useSearch();
  const setPageIndex = useTableStore((s) => s.setPageIndex);

  const separator = ' && ';
  const firstSearchPage = 1;

  const buildDateFilter = (from: number, to: number) => `createdAt:>=${from} && createdAt:<=${to}`;

  // Hydrate the picker from either engine's syntax — a stale filter may predate the backend flip.
  const parseCreatedAtRange = (filters: string) => ({
    fromMatch: filters.match(/createdAt\s*:?\s*>=\s*(\d+)/i),
    toMatch: filters.match(/createdAt\s*:?\s*<=\s*(\d+)/i),
  });

  const filters = uiState[searchIndexes.default]?.configure?.filters ?? '';

  useEffect(() => {
    if (!filters) {
      setDate(undefined);
      return;
    }
    const { fromMatch, toMatch } = parseCreatedAtRange(filters);
    if (fromMatch && toMatch) {
      setDate({
        from: startOfDay(fromUnixTime(parseInt(fromMatch[1], 10))),
        to: endOfDay(fromUnixTime(parseInt(toMatch[1], 10))),
      });
    } else {
      setDate(undefined);
    }
  }, [filters]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    // Table pagination is 0-based, but InstantSearch uiState.page is 1-based.
    // Reset both to their respective first-page values on date range changes.
    setPageIndex(EntityType.Asset, 0);

    if (range?.from && range?.to) {
      const fromDate = getUnixTime(range.from);
      const toDate = getUnixTime(range.to);

      const newDateFilter = buildDateFilter(fromDate, toDate);

      const existingFilters = splitFilterClauses(uiState[searchIndexes.default].configure?.filters ?? '')
        .filter((filter) => !isCreatedAtClause(filter))
        .join(separator)
        .trim();

      const combinedFilters = [existingFilters, newDateFilter].filter(Boolean).join(separator);

      void navigateToResults({
        [searchIndexes.default]: {
          ...uiState[searchIndexes.default],
          page: firstSearchPage,
          configure: {
            ...uiState[searchIndexes.default].configure,
            filters: combinedFilters,
          },
        },
      });
    } else {
      void navigateToResults({
        [searchIndexes.default]: {
          ...uiState[searchIndexes.default],
          page: firstSearchPage,
          configure: {
            ...uiState[searchIndexes.default].configure,
            filters: splitFilterClauses(uiState[searchIndexes.default].configure?.filters ?? '')
              .filter((filter) => !isCreatedAtClause(filter))
              .join(separator)
              .trim(),
          },
        },
      });
    }
  };

  const clearDateRange = () => {
    setDate(undefined);
    handleDateRangeChange(undefined);
    setIsOpen(false);
  };

  return (
    <DatePickerWithRange
      date={date}
      setDate={setDate}
      onDateChange={handleDateRangeChange}
      onClear={clearDateRange}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="border-0 ring-1 ring-ui-bevel ring-offset-0 transition duration-150 focus-visible:ring-2 focus-visible:ring-ui-separator! focus-visible:ring-offset-0 focus-visible:outline-hidden focus-visible:duration-150"
      dataCy="search-date-picker-with-range-button"
    />
  );
};
